import { ExternalLink, Instagram, Menu, Moon, Sun, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { Container } from './impeccable.js';

export const JOIN_URL = 'https://chat.whatsapp.com/FP0r7bW6nH2HfhTLMjOivN';
export const CONTACT_EMAIL = 'ezzabuzaid@gmail.com';
export const INSTAGRAM_URL = 'https://instagram.com/theagentlab.dev';

const navLinks = [
  { label: 'Community', href: '/#community' },
  { label: 'Events', href: '/events' },
  { label: 'Specialized Models', href: '/specialized-models' },
  { label: 'About', href: '/#about' },
];

export function FlaskIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 100 100"
      className={className}
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M40,10 C36,10 36,12 36,14 L36,34 C20,46 12,62 16,78 C20,90 34,96 50,96 C66,96 80,90 84,78 C88,62 80,46 64,34 L64,14 C64,12 64,10 60,10"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M34,10 C34,8 40,7 50,7 C60,7 66,8 66,10"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <path
        d="M22,68 C22,58 30,50 50,50 C70,50 78,58 78,68 C80,82 68,94 50,94 C32,94 20,82 22,68 Z"
        fill="#e84a00"
        opacity="0.15"
      />
      <text
        x="32"
        y="80"
        fontFamily="'Space Mono', monospace"
        fontSize="20"
        fontWeight="700"
        fill="#e84a00"
      >
        &gt;_
      </text>
      <circle
        cx="56"
        cy="44"
        r="3.5"
        fill="none"
        stroke="#e84a00"
        strokeWidth="1.5"
        opacity="0.8"
      />
      <circle
        cx="46"
        cy="34"
        r="2.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        opacity="0.5"
      />
      <circle
        cx="54"
        cy="24"
        r="1.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
        opacity="0.35"
      />
    </svg>
  );
}

export function useToggleTheme() {
  return useCallback(() => {
    const next = !document.documentElement.classList.contains('dark');
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
  }, []);
}

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleTheme = useToggleTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all ${
          scrolled
            ? 'border-border bg-background/80 border-b backdrop-blur-md'
            : 'bg-transparent'
        }`}
      >
        <Container>
          <div className="flex h-16 items-center justify-between">
            <a href="/" className="flex items-center gap-2 no-underline">
              <FlaskIcon className="text-foreground size-8" />
              <span className="font-heading text-foreground text-xl font-light">
                The Agent Lab
              </span>
            </a>

            <div className="hidden items-center gap-6 lg:flex">
              <nav className="flex items-center gap-6">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="text-muted-foreground hover:text-foreground text-sm font-medium no-underline transition-colors"
                  >
                    {link.label}
                  </a>
                ))}
              </nav>

              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="The Agent Lab on Instagram"
                className="text-muted-foreground hover:text-foreground  transition-colors"
              >
                <Instagram size={18} />
              </a>

              <button
                onClick={toggleTheme}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Toggle color theme"
              >
                <Moon size={18} className="dark:hidden" />
                <Sun size={18} className="hidden dark:block" />
              </button>

              <a
                href={JOIN_URL}
                className="bg-primary hover:bg-[#d44400] px-5 py-2 text-sm font-semibold text-white transition-colors rounded-sm"
              >
                Join the Community
              </a>
            </div>

            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 lg:hidden"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            >
              {menuOpen ? (
                <X size={20} className="text-foreground" />
              ) : (
                <Menu size={20} className="text-foreground" />
              )}
            </button>
          </div>
        </Container>
      </header>

      {menuOpen && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm lg:hidden"
            onClick={closeMenu}
          />
          <div className="border-border bg-background fixed top-0 right-0 bottom-0 z-50 w-full max-w-[min(320px,85vw)] border-l shadow-xl lg:hidden">
            <div className="flex h-16 items-center justify-end px-6">
              <button
                onClick={closeMenu}
                className="p-2"
                aria-label="Close menu"
              >
                <X size={20} className="text-foreground" />
              </button>
            </div>
            <nav className="flex flex-col gap-1 px-6">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={closeMenu}
                  className="border-border text-foreground border-b py-4 text-lg font-medium no-underline"
                >
                  {link.label}
                </a>
              ))}
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMenu}
                className="border-border text-foreground flex items-center gap-2 border-b py-4 text-lg font-medium no-underline"
              >
                <Instagram size={18} />
                Instagram
              </a>
              <div className="mt-4">
                <p className="text-muted-foreground font-mono text-xs tracking-wider uppercase">
                  Jordan
                </p>
              </div>
              <a
                href={JOIN_URL}
                onClick={closeMenu}
                className="bg-primary mt-4 px-5 py-3 text-center text-sm font-semibold text-white"
              >
                Join the Community
              </a>
            </nav>
          </div>
        </>
      )}
    </>
  );
}

export function Footer() {
  return (
    <footer className="border-border border-t py-12">
      <Container>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-muted-foreground font-mono text-xs tracking-wider uppercase">
            The Agent Lab · Jordan · theagentlab.dev
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-primary inline-flex items-center gap-1 font-mono text-xs tracking-wider uppercase underline"
            >
              Interested in sponsoring an event?
              <ExternalLink size={12} />
            </a>
            <span className="text-border hidden sm:inline">·</span>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-primary font-mono text-xs tracking-wider uppercase hover:underline"
            >
              Contact Us
            </a>
            <span className="text-border hidden sm:inline">·</span>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="The Agent Lab on Instagram"
              className="text-primary inline-flex items-center gap-1 font-mono text-xs tracking-wider uppercase hover:underline"
            >
              <Instagram size={12} />
              Instagram
            </a>
          </div>
        </div>
      </Container>
    </footer>
  );
}
